import type { IndustryDemoPack } from '../types'
import { enLabDemos } from '../lab-demos/en'

/**
 * Per-industry demo overrides for the IndustryDetailPage. Each industry's
 * `demos.<paradigm>` spreads the petroleum default and overrides only the
 * domain-specific fields. UI shell strings (button labels, headings)
 * inherit unchanged.
 */

const energy: IndustryDemoPack = {
  demos: {
    eventDriven: enLabDemos.eventDriven,
    goalDriven: enLabDemos.goalDriven,
    explainability: enLabDemos.explainability,
  },
}

// ───── Finance ─────

const finance: IndustryDemoPack = {
  demos: {
    riskScoring: {
      ...enLabDemos.riskScoring,
      controlLabel: 'Score Alpha Manufacturing Corp.',
      controlHint:
        'Adjust the three risk dimensions. The ontology aggregates weighted contributions into a single counterparty risk score linking back to the rules and data sources behind every number.',
      dimensionsTitle: 'Risk dimensions',
      dimensions: [
        { id: 'financial', label: 'Credit history', weightPct: 40, description: 'Days past due, defaults, payment-lag trend', defaultValue: 5 },
        { id: 'operational', label: 'Repayment capacity', weightPct: 35, description: 'Cash-flow coverage, leverage ratio, profit trend', defaultValue: 4 },
        { id: 'esg', label: 'KYC & sanctions', weightPct: 25, description: 'PEP screening, sanctions list, AML flag history', defaultValue: 3 },
      ],
      scoreTitle: 'Counterparty risk score',
      insightTitle: 'Audit-ready lending decisions',
      insightBody:
        'Risk committees see the chain from score → rule → data source in seconds. Lending-policy changes propagate to every active deal immediately, with an automatic before/after diff for the audit team.',
      contextSupplier: {
        label: 'Borrower under review',
        name: 'Alpha Manufacturing Corp.',
        detail: '8-year credit history · $120 M term-loan request · sector: industrial machinery',
      },
    },
    questionGrounded: {
      ...enLabDemos.questionGrounded,
      insightTitle: 'Loan answers, not loan documents',
      insightBody:
        'Vanilla RAG hands you a paragraph and three PDF citations. The ontology returns an entity-link chain — KYC fields, collateral data, signing authority — clickable down to the source system.',
      questions: [
        {
          id: 'q-approve-loan',
          question: 'Can I approve the $120 M term loan to Alpha Corp. this week?',
          answer:
            'Alpha Corp. is an [[entity:e1|approved counterparty]] with a [[entity:e2|credit score of 720]]. The $120 M ticket exceeds the [[entity:e3|$100 M VP threshold]], so the deal needs [[entity:e4|VP committee sign-off]] under [[entity:e5|R-CREDIT-AUTH-04]]. Collateral coverage at 142% satisfies [[entity:e6|R-COLL-130-08]], so the loan structure clears — pending committee schedule.',
          entities: [
            { id: 'e1', label: 'Approved counterparty', kind: 'object', detail: 'Onboarded 2018-03 · last KYC refresh 2024-09 · 12 prior facilities' },
            { id: 'e2', label: 'Credit score 720', kind: 'inference', detail: 'Bureau score · weighted with internal 24-month performance' },
            { id: 'e3', label: '$100 M VP threshold', kind: 'inference', detail: 'Currency-converted authority ceiling · reviewed quarterly' },
            { id: 'e4', label: 'VP committee sign-off', kind: 'action', detail: 'Tuesday-Thursday windows · 5 business-day SLA · escalation route documented' },
            { id: 'e5', label: 'R-CREDIT-AUTH-04', kind: 'constraint', detail: '≥ $100 M needs VP committee with quorum 3-of-5 voting members', citation: 'Group Credit Policy §2.4' },
            { id: 'e6', label: 'R-COLL-130-08', kind: 'constraint', detail: 'Collateral coverage ≥ 130% required for unsecured-equivalent risk weight', citation: 'Group Credit Policy §6.1' },
          ],
        },
        {
          id: 'q-sector-exposure',
          question: 'What is our sector-Y exposure across the loan book?',
          answer:
            'Sector Y total exposure stands at [[entity:e1|$2.4 B]] across [[entity:e2|34 active facilities]]. The [[entity:e3|R-CONC-SECTOR-12]] concentration cap is set at $3 B, so we have $600 M of headroom. Top three contributors: [[entity:e4|Alpha ($120 M)]], [[entity:e5|Gamma ($95 M)]], [[entity:e6|Delta ($80 M)]]. New deals in Y should land in the next quarterly review.',
          entities: [
            { id: 'e1', label: '$2.4 B', kind: 'inference', detail: 'Aggregated from active facilities · net of provisions · re-evaluates nightly' },
            { id: 'e2', label: '34 active facilities', kind: 'object', detail: 'Includes loans, guarantees, committed credit lines · sector classification via NAICS' },
            { id: 'e3', label: 'R-CONC-SECTOR-12', kind: 'constraint', detail: 'Cap 8% of total book per sector · review trigger at 7%', citation: 'Risk Appetite §3.2' },
            { id: 'e4', label: 'Alpha ($120 M)', kind: 'object', detail: 'Standard 12-month renewal · investment-grade' },
            { id: 'e5', label: 'Gamma ($95 M)', kind: 'object', detail: 'Watch-listed since 2025-01 · early-warning flag' },
            { id: 'e6', label: 'Delta ($80 M)', kind: 'object', detail: 'Recent extension · monitored quarterly' },
          ],
        },
        {
          id: 'q-counterparty',
          question: 'Why is counterparty Beta Trading flagged?',
          answer:
            'Beta Trading triggers [[entity:e1|R-EARLY-WARN-07]]: their parent entity reported [[entity:e2|a 38% earnings drop]] in Q1, and [[entity:e3|two missed coupon payments]] hit our ledger in the last 30 days. The graph also surfaces [[entity:e4|shared exposure with Gamma]] (also flagged), so concentration risk is amplified. [[entity:e5|Recommended action]]: tighten covenants and request updated financials within 14 days.',
          entities: [
            { id: 'e1', label: 'R-EARLY-WARN-07', kind: 'constraint', detail: 'Composite early-warning rule: parent earnings shock + missed payments + correlation', citation: 'Credit Risk Playbook §7' },
            { id: 'e2', label: '38% earnings drop', kind: 'inference', detail: 'Q1 reported earnings vs prior-year comparison · sourced from public filings' },
            { id: 'e3', label: 'Two missed coupon payments', kind: 'object', detail: 'Bond-coupon delays of 4 and 9 days · ledger system entries' },
            { id: 'e4', label: 'Shared exposure with Gamma', kind: 'inference', detail: 'Cross-counterparty correlation · graph traversal · Gamma also early-warning flagged' },
            { id: 'e5', label: 'Recommended action', kind: 'action', detail: 'Tighten covenants, request 14-day refresh, schedule credit-officer review' },
          ],
        },
      ],
    },
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'Stress-test rate sensitivity (basis points)',
      controlHint:
        'Drag to set the tolerance band. The ontology re-classifies every portfolio against the new threshold and surfaces which books would flip status.',
      thresholdLabel: 'Tolerance',
      unitLabel: 'bps',
      impactTitle: 'Impact across the loan book',
      impactSummaryTpl: '{affected} of {total} portfolios flipped band',
      statusLabels: {
        standard: 'Within tolerance',
        watch: 'Approaching limit',
        sour: 'Review triggered',
      },
      assetMetaLabel: 'Rate sensitivity',
      insightTitle: 'One rate change, full-portfolio propagation',
      insightBody:
        'A policy rate move once required a 3-day review project — analysts pulling spreadsheets from 4 systems. Now the ontology re-evaluates every portfolio and tags every in-flight credit decision in seconds, with the diff log attached for audit.',
      assets: [
        { id: 'fin-p1', code: 'PORT-A', name: 'SME industrial term loans', h2sPpm: 980 },
        { id: 'fin-p2', code: 'PORT-B', name: 'Commercial real-estate', h2sPpm: 760 },
        { id: 'fin-p3', code: 'PORT-C', name: 'Consumer auto loans', h2sPpm: 540 },
        { id: 'fin-p4', code: 'PORT-D', name: 'Trade finance · APAC', h2sPpm: 420 },
        { id: 'fin-p5', code: 'PORT-E', name: 'Sovereign EU', h2sPpm: 380 },
        { id: 'fin-p6', code: 'PORT-F', name: 'Investment-grade corp', h2sPpm: 280 },
        { id: 'fin-p7', code: 'PORT-G', name: 'Money market', h2sPpm: 180 },
        { id: 'fin-p8', code: 'PORT-H', name: 'Treasury hedges', h2sPpm: 120 },
        { id: 'fin-p9', code: 'PORT-I', name: 'Short-dated CP', h2sPpm: 80 },
        { id: 'fin-p10', code: 'PORT-J', name: 'Repo book', h2sPpm: 40 },
      ],
      currentThresholdPpm: 500,
      thresholdRange: { min: 50, max: 1200, step: 25 },
    },
  },
}

// ───── Healthcare ─────

const healthcare: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...enLabDemos.eventDriven,
      pendingLabel: 'Active alert',
      pendingHint: 'A patient-monitor alert sitting in the queue. Fire it to watch the ontology synthesise a clinical response.',
      event: {
        code: 'ALERT C-2104',
        meta: '07:32 · ICU-B · Bed 4',
        summary: 'Patient B12-Patel · sustained BP drop',
        descriptor: 'BP 110/70 → 78/45 over 8 min · MAP < 65',
        payloadLabel: 'Vitals stream',
        triggerNodeLabel: 'Event::ALERT_C-2104',
        triggerNodeDetail: 'Patient monitor CGS-MON-04 · severity class-A · 07:32:14 · auto-cleared overrides 2×',
      },
      goals: [
        { id: 'compliance', label: 'Stabilise per protocol' },
        { id: 'uptime', label: 'Diagnose & escalate' },
        { id: 'cost', label: 'Triage & discharge' },
      ],
      goalSelectorHint: 'Same alert, different goal → completely different response chain.',
      insightTitle: 'Bedside, not back-office',
      insightBody:
        'The chain — vitals, history, allergies, contraindications — is synthesised at alert time. Clinicians get a one-minute decision window without flipping between four systems.',
    },
    documentDriven: {
      ...enLabDemos.documentDriven,
      documentsLabel: 'Inbound patient documents',
      documentsHint: 'Click any clinical doc to "ingest" it. Watch the LLM pull entities, the ontology bind them, and the handling chain synthesise.',
      insightTitle: 'Same patient, different inputs, one chain',
      insightBody:
        'Triage, specialist match, pre-admission orders are synthesised from whichever doc lands — no manual coordinator stitching EHR, lab, RIS, and PACS records together.',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'Referral-B12-Patel.pdf',
          meta: '4 pages · uploaded by ED',
          summary: 'Internal medicine referral · 68 yo · post-stent',
          entities: [
            { kind: 'Patient', value: 'B12-Patel · 68 yo M' },
            { kind: 'Acute', value: 'Chest pain · DOE' },
            { kind: 'History', value: 'STEMI 2023 · LAD stent' },
            { kind: 'Allergies', value: 'Sulfa drugs · severity 2' },
            { kind: 'Meds', value: 'Aspirin · Statin · Metoprolol' },
          ],
        },
        {
          id: 'rfq',
          icon: '🧪',
          name: 'Lab-CBC-2104.xml',
          meta: 'HL7 · uploaded by lab system',
          summary: 'Routine CBC + metabolic panel · flagged abnormal',
          entities: [
            { kind: 'Patient', value: 'B12-Patel' },
            { kind: 'Panel', value: 'CBC + BMP' },
            { kind: 'Flagged', value: 'K = 2.8 mmol/L (low)' },
            { kind: 'Reference', value: '3.5-5.0 mmol/L' },
            { kind: 'Priority', value: 'STAT review' },
          ],
        },
        {
          id: 'incident',
          icon: '🩻',
          name: 'CT-chest-2104.dcm',
          meta: 'DICOM · uploaded by radiology',
          summary: 'Non-contrast chest CT · 64 slices · flagged opacity',
          entities: [
            { kind: 'Patient', value: 'B12-Patel' },
            { kind: 'Study', value: 'Non-contrast chest CT' },
            { kind: 'Finding', value: '2.1 cm RML opacity' },
            { kind: 'Follow-up', value: 'PET / biopsy recommended' },
            { kind: 'Comparison', value: 'No prior imaging on file' },
          ],
        },
      ],
    },
    explainability: {
      ...enLabDemos.explainability,
      insightTitle: 'Care audit by construction',
      insightBody:
        'Every clinical decision carries the chain — guideline citation, lab value, allergy, contraindication. Defensive medicine becomes defensible medicine: malpractice review starts with the graph, not a chart-mining project.',
      decisions: [
        { id: 'decision-act-viol', question: '"Why was drug X not prescribed?"', goalId: 'compliance', nodeId: 'g1-act' },
        { id: 'decision-c-plan', question: '"Why was the patient escalated to ICU?"', goalId: 'compliance', nodeId: 'g1-c-plan' },
        { id: 'decision-c-mat', question: '"Why was this patient flagged for sepsis review?"', goalId: 'compliance', nodeId: 'g1-c-mat' },
        { id: 'decision-cand-viol', question: '"Why was the discharge delayed?"', goalId: 'compliance', nodeId: 'g1-cand' },
        { id: 'decision-uptime-act', question: '"Why was the protocol-violation override approved?"', goalId: 'uptime', nodeId: 'g2-act' },
      ],
    },
  },
}

// ───── Legal ─────

const legal: IndustryDemoPack = {
  demos: {
    documentDriven: {
      ...enLabDemos.documentDriven,
      documentsLabel: 'Inbound legal documents',
      documentsHint: 'Click any doc to "ingest" it. Watch the LLM pull clauses, the ontology bind them to obligations, and the review chain synthesise.',
      insightTitle: 'Three docs, three review chains, same ontology',
      insightBody:
        'NDA / MSA / regulatory filing all flow through the same ontology — different clause types fire different review chains. New regulation lands → contracts already in the system get re-flagged automatically.',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'NDA-XCorp-2025.docx',
          meta: '6 pages · uploaded by Legal',
          summary: 'Mutual NDA with X Corp · 3-year term · IP-heavy',
          entities: [
            { kind: 'Parties', value: 'Acme Inc. ⇄ X Corp' },
            { kind: 'Term', value: '36 months · auto-renewal' },
            { kind: 'Scope', value: 'IP, source code, customer lists' },
            { kind: 'Carve-outs', value: 'Independently developed (§4)' },
            { kind: 'Governing law', value: 'Delaware · exclusive forum' },
          ],
        },
        {
          id: 'rfq',
          icon: '📋',
          name: 'MSA-Acme-v3.pdf',
          meta: '42 pages · uploaded by Procurement',
          summary: 'Master Services Agreement · Acme Pumps · 5-year',
          entities: [
            { kind: 'Parties', value: 'Acme Inc. ⇄ Acme Pumps' },
            { kind: 'SOWs', value: '12 anticipated · $40 M cap' },
            { kind: 'Annual cap', value: '$8 M / year' },
            { kind: 'Liability', value: '2× annual cap (uncapped IP)' },
            { kind: 'Renewal', value: 'Auto-renewing · 90-day notice' },
          ],
        },
        {
          id: 'incident',
          icon: '⚠',
          name: 'GDPR-DPA-2025-Q2.docx',
          meta: '18 pages · uploaded by DPO',
          summary: 'Data Processing Addendum · GDPR-aligned · APAC processor',
          entities: [
            { kind: 'Processor', value: 'Acme APAC Pte. Ltd.' },
            { kind: 'Sub-processors', value: '3 listed · 2 cloud · 1 support' },
            { kind: 'Data', value: 'PII · transaction logs · KYC' },
            { kind: 'Transfer basis', value: 'SCCs + UK addendum' },
            { kind: 'Audit', value: 'Annual · 14-day notice' },
          ],
        },
      ],
    },
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'Privacy regulation strictness (data categories covered)',
      controlHint:
        'Drag the slider to model a new regulation expanding (or relaxing) the set of regulated data categories. The ontology immediately re-evaluates every contract and process.',
      thresholdLabel: 'Categories covered',
      unitLabel: 'categories',
      impactTitle: 'Impact across active contracts',
      impactSummaryTpl: '{affected} of {total} contracts flipped status',
      statusLabels: {
        standard: 'Compliant',
        watch: 'Review advised',
        sour: 'Update required',
      },
      assetMetaLabel: 'Data categories used',
      insightTitle: 'One rule change, contract-wide propagation',
      insightBody:
        'Regulators update the data-category list — the ontology surfaces every contract that needs revising, with a draft clause patch attached. Legal teams stop running grep across SharePoint and start with a triaged work-list.',
      assets: [
        { id: 'leg-c1', code: 'C-201', name: 'EU customer data processor', h2sPpm: 220 },
        { id: 'leg-c2', code: 'C-218', name: 'Marketing analytics (DMP)', h2sPpm: 180 },
        { id: 'leg-c3', code: 'C-244', name: 'Employee benefits portal', h2sPpm: 160 },
        { id: 'leg-c4', code: 'C-261', name: 'HR data hosting', h2sPpm: 140 },
        { id: 'leg-c5', code: 'C-272', name: 'Customer support tooling', h2sPpm: 120 },
        { id: 'leg-c6', code: 'C-288', name: 'KYC document repository', h2sPpm: 100 },
        { id: 'leg-c7', code: 'C-301', name: 'Vendor onboarding portal', h2sPpm: 80 },
        { id: 'leg-c8', code: 'C-310', name: 'Procurement system', h2sPpm: 60 },
        { id: 'leg-c9', code: 'C-322', name: 'Public marketing site', h2sPpm: 40 },
        { id: 'leg-c10', code: 'C-331', name: 'Investor relations site', h2sPpm: 20 },
      ],
      currentThresholdPpm: 100,
      thresholdRange: { min: 10, max: 250, step: 5 },
    },
    questionGrounded: {
      ...enLabDemos.questionGrounded,
      insightTitle: 'Sign-or-stop answers',
      insightBody:
        'Legal review used to be "let me get back to you in 3 days". With a grounded answer, every clause links to the rule and the precedent — sign-or-stop in under a minute.',
      questions: [
        {
          id: 'q-sign-contract',
          question: 'Can I sign the Acme MSA today?',
          answer:
            'The MSA is a [[entity:e1|5-year master with $8 M annual cap]]. Total value over term clears the [[entity:e2|$10 M VP threshold]] under [[entity:e3|R-SIG-AUTH-01]], so [[entity:e4|VP signature]] is required. The [[entity:e5|liability clause §11.4]] is 2× annual cap with an uncapped IP carve-out — standard for our template. Sign once VP signs the DocuSign envelope already in flight.',
          entities: [
            { id: 'e1', label: '5-year master with $8 M annual cap', kind: 'object', detail: '$40 M aggregate · auto-renewing · 90-day notice' },
            { id: 'e2', label: '$10 M VP threshold', kind: 'inference', detail: 'Currency-converted authority ceiling · reviewed quarterly' },
            { id: 'e3', label: 'R-SIG-AUTH-01', kind: 'constraint', detail: '≥ $10 M aggregate → VP signature + board notify if multi-year', citation: 'Group PA-2024-117 §3.1' },
            { id: 'e4', label: 'VP signature', kind: 'action', detail: 'In-flight DocuSign envelope · VP queue · SLA 2 business days' },
            { id: 'e5', label: 'Liability clause §11.4', kind: 'object', detail: '2× annual cap · uncapped for IP infringement · standard template' },
          ],
        },
        {
          id: 'q-jurisdiction',
          question: 'Which jurisdictions does this clause apply in?',
          answer:
            'Clause §17 (force majeure) is [[entity:e1|governed by Delaware law]] but the contract carries an [[entity:e2|APAC carve-out (§17.4)]] that defers to local law for events occurring in Singapore, Hong Kong, or Japan. [[entity:e3|Conflict-resolution rule R-CONFL-LAW-02]] selects whichever law gives the more restrictive interpretation. [[entity:e4|Recommended path]]: route Asia-Pacific incidents through the regional GC office.',
          entities: [
            { id: 'e1', label: 'Governed by Delaware law', kind: 'constraint', detail: '§22 master choice-of-law · exclusive forum in Wilmington' },
            { id: 'e2', label: 'APAC carve-out (§17.4)', kind: 'object', detail: 'Subordinates §17 to local law for SG/HK/JP events' },
            { id: 'e3', label: 'R-CONFL-LAW-02', kind: 'constraint', detail: 'When jurisdictions overlap, more restrictive law applies', citation: 'Legal Playbook §4.2' },
            { id: 'e4', label: 'Recommended path', kind: 'action', detail: 'Route APAC incidents through regional GC · 5-day SLA' },
          ],
        },
        {
          id: 'q-gdpr-export',
          question: 'Are we compliant exporting customer data to APAC?',
          answer:
            'The export operates under [[entity:e1|EU SCCs (2021) + UK addendum]]. APAC adequacy is not granted, so we additionally rely on [[entity:e2|R-TIA-2023-04]] (transfer impact assessment, refreshed 2025-Q1). [[entity:e3|Sub-processors]] in scope: 3, all listed in the DPA. [[entity:e4|Pending gap]]: Singapore PDPA notice update for 2 customer segments — scheduled for next quarter.',
          entities: [
            { id: 'e1', label: 'EU SCCs (2021) + UK addendum', kind: 'object', detail: 'Module 2 (controller→processor) · executed 2024-03 · 2-year refresh cycle' },
            { id: 'e2', label: 'R-TIA-2023-04', kind: 'constraint', detail: 'Transfer impact assessment refreshed annually · risk-rated low for APAC', citation: 'Privacy Playbook §6' },
            { id: 'e3', label: 'Sub-processors', kind: 'object', detail: '3 listed: 2 cloud (AWS, Azure), 1 support (Acme APAC)' },
            { id: 'e4', label: 'Pending gap', kind: 'action', detail: 'PDPA notice update · 2 customer segments · scheduled 2026-Q3' },
          ],
        },
      ],
    },
  },
}

// ───── Automotive ─────

const automotive: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...enLabDemos.eventDriven,
      pendingLabel: 'Line alert',
      pendingHint: 'A vibration anomaly on a welding robot. Fire it to watch the ontology trace from sensor → defect rule → quality response.',
      event: {
        code: 'ALERT V-7188',
        meta: '14:22 · Plant-2 · Bay 9 · Robot R-031',
        summary: 'Welding-robot vibration spike',
        descriptor: 'Spindle vibration 4.2 mm/s → 11.8 mm/s · Δ +180% in 12 s',
        payloadLabel: 'PLC telemetry stream',
        triggerNodeLabel: 'Event::ALERT_V-7188',
        triggerNodeDetail: 'PLC sensor RT-VIB-031 · severity class-B · 14:22:08 · trend escalating',
      },
      goals: [
        { id: 'compliance', label: 'Quality protocol' },
        { id: 'uptime', label: 'Keep line running' },
        { id: 'cost', label: 'Cost-of-defect' },
      ],
      goalSelectorHint: 'Same vibration spike, different goal → completely different response chain.',
      insightTitle: 'Catch the defect before the next 60 cars',
      insightBody:
        'Without a chain the line keeps running and finds the defect at end-of-line QA — 60 cars and 4 hours of rework. With the ontology the chain fires inside one cycle: defect type, recall-rule, downstream stations notified, work-order drafted.',
    },
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'ECU firmware patch (severity threshold)',
      controlHint:
        'Drag the slider to set the patch severity. The ontology re-classifies every VIN against the new threshold and surfaces the recall-eligible cohort.',
      thresholdLabel: 'Severity',
      unitLabel: 'CVSS',
      impactTitle: 'Impact across the VIN base',
      impactSummaryTpl: '{affected} of {total} VIN cohorts flipped status',
      statusLabels: {
        standard: 'No action',
        watch: 'OTA push',
        sour: 'Service-bay recall',
      },
      assetMetaLabel: 'Affected severity score',
      insightTitle: 'One firmware change, fleet-wide propagation',
      insightBody:
        'A CVSS update on the ECU firmware lands — the ontology maps it to ECU SKU, the SKU to model years, model years to VINs, VINs to dealers, dealers to service capacity. What used to be a 3-week recall planning project lands as a triaged work-list in seconds.',
      assets: [
        { id: 'auto-c1', code: 'COHORT-A', name: 'Model X 2023 · ECU-v2.1', h2sPpm: 9 },
        { id: 'auto-c2', code: 'COHORT-B', name: 'Model X 2022 · ECU-v2.0', h2sPpm: 8 },
        { id: 'auto-c3', code: 'COHORT-C', name: 'Model Y 2023 · ECU-v2.1', h2sPpm: 7 },
        { id: 'auto-c4', code: 'COHORT-D', name: 'Model Y 2022 · ECU-v1.9', h2sPpm: 6 },
        { id: 'auto-c5', code: 'COHORT-E', name: 'Model Z 2024 · ECU-v3.0', h2sPpm: 5 },
        { id: 'auto-c6', code: 'COHORT-F', name: 'Model Z 2023 · ECU-v2.8', h2sPpm: 4 },
        { id: 'auto-c7', code: 'COHORT-G', name: 'Truck T 2024 · ECU-v3.0', h2sPpm: 3 },
        { id: 'auto-c8', code: 'COHORT-H', name: 'Truck T 2023 · ECU-v2.9', h2sPpm: 2 },
        { id: 'auto-c9', code: 'COHORT-I', name: 'Van V 2024 · ECU-v3.0', h2sPpm: 1 },
        { id: 'auto-c10', code: 'COHORT-J', name: 'Van V 2023 · ECU-v2.8', h2sPpm: 1 },
      ],
      currentThresholdPpm: 5,
      thresholdRange: { min: 1, max: 10, step: 1 },
    },
    whatIf: {
      ...enLabDemos.whatIf,
      insightTitle: 'Battery degradation, forward-projected',
      insightBody:
        'Each EV battery has a sensor-derived state-of-health. The ontology projects degradation curves under different usage scenarios and flips a cohort to "service-eligible" the moment the curve crosses warranty thresholds — months before the dealer would see it on the lot.',
      scenarios: [
        { id: 'normal', title: 'Mixed urban / highway', description: 'Baseline usage profile · climate-controlled garage', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: 'Heavy fast-charge', description: 'Daily DC fast-charge · short urban cycles', h2sGrowthPctPerMonth: 3 },
        { id: 'extreme', title: 'Hot-climate fleet', description: 'Outdoor parking · ambient ≥ 35 °C avg · 2 charges/day', h2sGrowthPctPerMonth: 5 },
      ],
      baseThresholdPpm: 80,
      assets: [
        { id: 'auto-b1', code: 'BATCH-1', name: 'Q1-2022 cell lot · 12k packs', h2sPpmStart: 95 },
        { id: 'auto-b2', code: 'BATCH-2', name: 'Q2-2022 cell lot · 18k packs', h2sPpmStart: 92 },
        { id: 'auto-b3', code: 'BATCH-3', name: 'Q3-2022 cell lot · 14k packs', h2sPpmStart: 88 },
        { id: 'auto-b4', code: 'BATCH-4', name: 'Q4-2022 cell lot · 16k packs', h2sPpmStart: 85 },
        { id: 'auto-b5', code: 'BATCH-5', name: 'Q1-2023 cell lot · 21k packs', h2sPpmStart: 90 },
        { id: 'auto-b6', code: 'BATCH-6', name: 'Q2-2023 cell lot · 20k packs', h2sPpmStart: 87 },
        { id: 'auto-b7', code: 'BATCH-7', name: 'Q3-2023 cell lot · 19k packs', h2sPpmStart: 84 },
        { id: 'auto-b8', code: 'BATCH-8', name: 'Q4-2023 cell lot · 22k packs', h2sPpmStart: 82 },
      ],
    },
  },
}

// ───── Logistics ─────

const logistics: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...enLabDemos.eventDriven,
      pendingLabel: 'Shipment exception',
      pendingHint: 'A container hold at port. Fire it to watch the ontology synthesise the re-route chain.',
      event: {
        code: 'EVT SH-4421',
        meta: '06:08 · Port Singapore · MSCU-7841',
        summary: 'Container customs hold · cold chain item',
        descriptor: 'Hold reason · paperwork mismatch · expected release 36–48h',
        payloadLabel: 'TMS event stream',
        triggerNodeLabel: 'Event::SH_4421',
        triggerNodeDetail: 'TMS adapter MSC-EDI · severity class-A · 06:08:42 · cold-chain priority',
      },
      goals: [
        { id: 'compliance', label: 'Hold cold chain' },
        { id: 'uptime', label: 'Meet customer SLA' },
        { id: 'cost', label: 'Minimise demurrage' },
      ],
      goalSelectorHint: 'Same hold, different goal → completely different recovery plan.',
      insightTitle: 'Move the cargo, not the email chain',
      insightBody:
        'Ops used to broker 6 emails across carrier, broker, warehouse, customer service. The ontology synthesises the chain in seconds — hold reason, SLA impact, alternative routing, customer comms draft — and the dispatcher just confirms.',
    },
    multiAgent: {
      ...enLabDemos.multiAgent,
      incidentLabel: 'Active incident',
      incidentTitle: 'Order #ORD-88241 — multi-stop fulfilment',
      incidentDetail:
        '3 SKUs · 2 carriers · cross-border (SG → HK → CN) · 48h commit · 1 cold-chain leg.',
      injectButton: 'Inject order',
      insightTitle: 'Five agents, one plan, full audit trail',
      insightBody:
        'Carrier-pick, customs-broker, warehouse, last-mile, comms — each is its own agent posting decisions to the ontology. The graph reconstructs the full coordination chain post-hoc, so the SLA-recovery review starts with facts, not a Slack archaeology dig.',
      agents: [
        { id: 'a-carrier', icon: '🚚', name: 'Carrier picker', accent: 'amber' },
        { id: 'a-broker', icon: '🛃', name: 'Customs broker', accent: 'cyan' },
        { id: 'a-warehouse', icon: '🏬', name: 'Warehouse', accent: 'violet' },
        { id: 'a-lastmile', icon: '🛵', name: 'Last-mile', accent: 'pass' },
        { id: 'a-comms', icon: '📣', name: 'Customer comms', accent: 'info' },
      ],
    },
    whatIf: {
      ...enLabDemos.whatIf,
      insightTitle: 'Stress-test the SLA, not the spreadsheet',
      insightBody:
        'Port congestion, fuel-price shocks, weather windows all flow through the ontology as scenario parameters. Service-level promises get re-projected lane-by-lane, and the at-risk cohorts surface with proposed mitigations — before the customer files a claim.',
      scenarios: [
        { id: 'normal', title: 'Baseline ops', description: 'Current schedule · normal port throughput', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: 'Port congestion +30%', description: 'Singapore + Long Beach congestion build-up', h2sGrowthPctPerMonth: 4 },
        { id: 'extreme', title: 'Typhoon window', description: 'Annual typhoon disruption · 7-day port closure', h2sGrowthPctPerMonth: 7 },
      ],
      baseThresholdPpm: 100,
      assets: [
        { id: 'log-l1', code: 'LANE-1', name: 'SG → CN cross-border', h2sPpmStart: 110 },
        { id: 'log-l2', code: 'LANE-2', name: 'HK → JP air-freight', h2sPpmStart: 95 },
        { id: 'log-l3', code: 'LANE-3', name: 'US-W → EU ocean', h2sPpmStart: 88 },
        { id: 'log-l4', code: 'LANE-4', name: 'EU intra · road', h2sPpmStart: 78 },
        { id: 'log-l5', code: 'LANE-5', name: 'APAC last-mile', h2sPpmStart: 72 },
        { id: 'log-l6', code: 'LANE-6', name: 'NA same-day', h2sPpmStart: 65 },
        { id: 'log-l7', code: 'LANE-7', name: 'LATAM domestic', h2sPpmStart: 60 },
        { id: 'log-l8', code: 'LANE-8', name: 'AU domestic', h2sPpmStart: 55 },
      ],
    },
  },
}

// ───── Government ─────

const government: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'Zoning policy strictness (allowable FAR)',
      controlHint:
        'Drag to set the new floor-area ratio (FAR) ceiling. The ontology re-evaluates every active permit and surfaces which projects breach the new rule.',
      thresholdLabel: 'FAR ceiling',
      unitLabel: 'FAR',
      impactTitle: 'Impact across active permits',
      impactSummaryTpl: '{affected} of {total} permits flipped status',
      statusLabels: {
        standard: 'Compliant',
        watch: 'Variance advisable',
        sour: 'Re-submission required',
      },
      assetMetaLabel: 'Permit FAR (×100)',
      insightTitle: 'One rule change, permit-wide propagation',
      insightBody:
        'Council updates the zoning code — the ontology surfaces every permit affected, with the variance pathway pre-drafted. Citizens see a clear before/after on the portal, council members see the impact in the chamber, and the audit log is built in.',
      assets: [
        { id: 'gov-p1', code: 'PRM-2201', name: 'Riverside mixed-use Tower A', h2sPpm: 480 },
        { id: 'gov-p2', code: 'PRM-2233', name: 'Downtown office expansion', h2sPpm: 420 },
        { id: 'gov-p3', code: 'PRM-2287', name: 'Logistics park · phase 2', h2sPpm: 380 },
        { id: 'gov-p4', code: 'PRM-2305', name: 'Mid-rise residential complex', h2sPpm: 340 },
        { id: 'gov-p5', code: 'PRM-2348', name: 'School expansion', h2sPpm: 300 },
        { id: 'gov-p6', code: 'PRM-2399', name: 'Community center', h2sPpm: 260 },
        { id: 'gov-p7', code: 'PRM-2412', name: 'Local retail block', h2sPpm: 220 },
        { id: 'gov-p8', code: 'PRM-2455', name: 'Single-family infill', h2sPpm: 180 },
        { id: 'gov-p9', code: 'PRM-2478', name: 'Heritage renovation', h2sPpm: 140 },
        { id: 'gov-p10', code: 'PRM-2491', name: 'Public-park kiosk', h2sPpm: 100 },
      ],
      currentThresholdPpm: 350,
      thresholdRange: { min: 50, max: 600, step: 25 },
    },
    continuousAudit: {
      ...enLabDemos.continuousAudit,
      insightTitle: 'Audit as a heartbeat, not a project',
      insightBody:
        'Service-delivery KPIs, permit-cycle times, procurement-vendor checks all run continuously through the ontology. Auditors stop sampling once a year and start with a live triaged backlog every morning.',
    },
    multiAgent: {
      ...enLabDemos.multiAgent,
      incidentLabel: 'Active incident',
      incidentTitle: 'INC-MULTI-2106 — multi-agency response',
      incidentDetail:
        'Industrial fire near residential block · evacuation needed · transit re-route · hospital triage warning.',
      injectButton: 'Trigger drill',
      insightTitle: 'Five agencies, one chain, no swivel-chair',
      insightBody:
        'Fire, police, transit, health, and the unified command each post into a shared ontology. The chain — who decided what, when, on what evidence — is the post-incident review.',
      agents: [
        { id: 'a-fire', icon: '🚒', name: 'Fire dept', accent: 'amber' },
        { id: 'a-police', icon: '🚓', name: 'Police', accent: 'cyan' },
        { id: 'a-transit', icon: '🚇', name: 'Transit', accent: 'violet' },
        { id: 'a-health', icon: '🏥', name: 'Health / EMS', accent: 'pass' },
        { id: 'a-eoc', icon: '🛰', name: 'Unified command', accent: 'info' },
      ],
    },
  },
}

// ───── Insurance ─────

const insurance: IndustryDemoPack = {
  demos: {
    documentDriven: {
      ...enLabDemos.documentDriven,
      documentsLabel: 'Inbound claim documents',
      documentsHint: 'Click any doc to "ingest" it. Watch the LLM pull entities, the ontology bind them to the policy and rules, and the claim chain synthesise.',
      insightTitle: 'First notice of loss → coverage decision in seconds',
      insightBody:
        'Police report, repair estimate, FNOL form all flow through the same ontology. Policy clauses, deductibles, exclusions and prior-claim history get bound up at ingestion — adjusters open a triaged decision, not an evidence pile.',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'FNOL-2104-P12.pdf',
          meta: '3 pages · uploaded by claims portal',
          summary: 'Auto collision · single-vehicle · property damage',
          entities: [
            { kind: 'Policy', value: 'AUTO-220914-P12' },
            { kind: 'Insured', value: 'J. Patel · clean record 6 yr' },
            { kind: 'Loss date', value: '2026-05-12' },
            { kind: 'Cause', value: 'Skid · wet road · single car' },
            { kind: 'Est. damage', value: '$4.8k front quarter' },
          ],
        },
        {
          id: 'rfq',
          icon: '👮',
          name: 'PoliceReport-NCT-7841.pdf',
          meta: '5 pages · uploaded by adjuster',
          summary: 'Officer report · no citation · no third party',
          entities: [
            { kind: 'Officer', value: 'Badge 4112 · on-scene' },
            { kind: 'Conditions', value: 'Wet road · dusk' },
            { kind: 'Citations', value: 'None issued' },
            { kind: 'Witnesses', value: '0 witnesses' },
            { kind: 'Hazards', value: 'No DUI · no suspected fraud' },
          ],
        },
        {
          id: 'incident',
          icon: '🔧',
          name: 'RepairEstimate-RE-9023.pdf',
          meta: '2 pages · uploaded by approved shop',
          summary: 'Body shop estimate · OEM parts',
          entities: [
            { kind: 'Shop', value: 'NCT-Approved Body Co. · Tier 1' },
            { kind: 'Parts', value: 'OEM bumper · OEM headlamp' },
            { kind: 'Labour', value: '12 hrs · $95/hr' },
            { kind: 'Total', value: '$4,820 incl. paint' },
            { kind: 'Timeline', value: '5 days · loaner included' },
          ],
        },
      ],
    },
    eventDriven: {
      ...enLabDemos.eventDriven,
      pendingLabel: 'CAT event spike',
      pendingHint: 'A storm-triggered claim surge. Fire it to watch the ontology bucket FNOLs, route to adjusters, and pre-stage reserves.',
      event: {
        code: 'CAT-2614',
        meta: '03:18 · NCT region · Storm Alma',
        summary: '+1,840 FNOLs · ↑6× baseline',
        descriptor: 'Wind 95 mph · 4-county footprint · ETA peak claims +72h',
        payloadLabel: 'NWS + FNOL stream',
        triggerNodeLabel: 'Event::CAT_2614',
        triggerNodeDetail: 'NWS feed + portal · severity class-A · 03:18:02 · escalation rule R-CAT-01',
      },
      goals: [
        { id: 'compliance', label: 'Reserves + reporting' },
        { id: 'uptime', label: 'Adjuster routing' },
        { id: 'cost', label: 'Fraud triage' },
      ],
      goalSelectorHint: 'Same CAT event, different goal → completely different response chain.',
      insightTitle: 'CAT response without a war room',
      insightBody:
        'CAT events used to trigger a 48-hour war room: spreadsheets across underwriting, claims, reinsurance, regulators. The ontology routes claims, pre-stages reserves, and updates the regulator-facing report in real time — no swivel-chair.',
    },
    riskScoring: {
      ...enLabDemos.riskScoring,
      controlLabel: 'Score commercial property: 12 Lakeside Plaza',
      controlHint:
        'Adjust the three risk dimensions. The ontology aggregates weighted contributions into a single underwriting score linking back to the rules and data sources behind every number.',
      dimensionsTitle: 'Underwriting dimensions',
      dimensions: [
        { id: 'financial', label: 'Hazard exposure', weightPct: 40, description: 'Flood / wildfire / wind zone + historical losses', defaultValue: 5 },
        { id: 'operational', label: 'Property condition', weightPct: 35, description: 'Age, roof age, fire suppression, security', defaultValue: 4 },
        { id: 'esg', label: 'Account behavior', weightPct: 25, description: 'Prior claims, payment history, named-insured profile', defaultValue: 3 },
      ],
      scoreTitle: 'Underwriting risk score',
      insightTitle: 'Underwriting that explains itself',
      insightBody:
        'Brokers used to receive "yes / no / refer" without context. Now the score links back to the hazard map, the property inspection, and the prior-claim history — and a binder can be drafted in the same screen.',
      contextSupplier: {
        label: 'Property under review',
        name: '12 Lakeside Plaza · commercial mixed-use',
        detail: 'Built 1998 · 4 stories · sprinkler-equipped · $14M TIV',
      },
    },
  },
}

// ───── Retail ─────

const retail: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'Category margin threshold (%)',
      controlHint:
        'Drag to model a margin-floor change. The ontology re-evaluates every SKU against the new floor and surfaces the cohort that needs price or promo adjustment.',
      thresholdLabel: 'Margin floor',
      unitLabel: '%',
      impactTitle: 'Impact across active SKUs',
      impactSummaryTpl: '{affected} of {total} SKU groups need re-pricing',
      statusLabels: {
        standard: 'Above floor',
        watch: 'Promo review',
        sour: 'Price reset',
      },
      assetMetaLabel: 'Current margin %',
      insightTitle: 'One margin rule, catalog-wide propagation',
      insightBody:
        'Margin policies used to live in spreadsheets that no one synced. The ontology binds each SKU to its cost, promo, and category rules — change the floor and the system surfaces who needs a re-price, a promo retire, or a vendor renegotiation.',
      assets: [
        { id: 'ret-s1', code: 'CAT-A1', name: 'Premium dairy', h2sPpm: 32 },
        { id: 'ret-s2', code: 'CAT-A2', name: 'Snack bars', h2sPpm: 28 },
        { id: 'ret-s3', code: 'CAT-A3', name: 'Cleaning · branded', h2sPpm: 25 },
        { id: 'ret-s4', code: 'CAT-A4', name: 'Frozen ready meals', h2sPpm: 22 },
        { id: 'ret-s5', code: 'CAT-A5', name: 'Pet food premium', h2sPpm: 20 },
        { id: 'ret-s6', code: 'CAT-B1', name: 'Beverages mainstream', h2sPpm: 16 },
        { id: 'ret-s7', code: 'CAT-B2', name: 'Bakery in-store', h2sPpm: 14 },
        { id: 'ret-s8', code: 'CAT-B3', name: 'Private-label dry', h2sPpm: 12 },
        { id: 'ret-s9', code: 'CAT-B4', name: 'Produce staples', h2sPpm: 10 },
        { id: 'ret-s10', code: 'CAT-B5', name: 'Bulk household', h2sPpm: 8 },
      ],
      currentThresholdPpm: 18,
      thresholdRange: { min: 5, max: 40, step: 1 },
    },
    whatIf: {
      ...enLabDemos.whatIf,
      insightTitle: 'Demand projection, by category',
      insightBody:
        'Promo planning used to be backward-looking. The ontology projects category demand under different scenarios — promo cadence, weather, competitor entry — and flips a category to "stockout-risk" the moment the forward curve crosses the safety threshold.',
      scenarios: [
        { id: 'normal', title: 'Baseline cadence', description: 'Current promo schedule · normal weather', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: 'Aggressive promo', description: 'Doubled promo intensity · loyalty push', h2sGrowthPctPerMonth: 4 },
        { id: 'extreme', title: 'Heat-wave + comp entry', description: 'Sustained heat + new competitor in 3 markets', h2sGrowthPctPerMonth: 7 },
      ],
      baseThresholdPpm: 90,
      assets: [
        { id: 'ret-d1', code: 'CAT-1', name: 'Beverages cold', h2sPpmStart: 95 },
        { id: 'ret-d2', code: 'CAT-2', name: 'Ice cream', h2sPpmStart: 92 },
        { id: 'ret-d3', code: 'CAT-3', name: 'Snacks salty', h2sPpmStart: 88 },
        { id: 'ret-d4', code: 'CAT-4', name: 'Sun-care', h2sPpmStart: 84 },
        { id: 'ret-d5', code: 'CAT-5', name: 'Outdoor grill', h2sPpmStart: 80 },
        { id: 'ret-d6', code: 'CAT-6', name: 'Bakery', h2sPpmStart: 70 },
        { id: 'ret-d7', code: 'CAT-7', name: 'Frozen', h2sPpmStart: 65 },
        { id: 'ret-d8', code: 'CAT-8', name: 'Dairy', h2sPpmStart: 60 },
      ],
    },
    continuousAudit: {
      ...enLabDemos.continuousAudit,
      insightTitle: 'Shrinkage & anomaly, continuous',
      insightBody:
        'POS audits used to run nightly batch. The ontology evaluates every transaction stream against rules — refund anomalies, voids, weight-mismatches, gift-card patterns — and posts a triaged backlog to loss-prevention in real time.',
    },
  },
}

// ───── Real Estate ─────

const realestate: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...enLabDemos.changePropagation,
      controlLabel: 'Benchmark rate (bps)',
      controlHint:
        'Drag the slider to model a benchmark rate move. The ontology re-classifies every mortgage tranche against the new cost-of-funds and surfaces the cohort that flips status.',
      thresholdLabel: 'Rate',
      unitLabel: 'bps',
      impactTitle: 'Impact across mortgage book',
      impactSummaryTpl: '{affected} of {total} tranches flipped status',
      statusLabels: {
        standard: 'Within band',
        watch: 'Yield warning',
        sour: 'Mark-to-market hit',
      },
      assetMetaLabel: 'Tranche rate sensitivity',
      insightTitle: 'One rate move, portfolio-wide propagation',
      insightBody:
        'Treasury used to wait 5 days for the loan-book to re-mark. The ontology binds every tranche to its underlying property, LTV, and product type — a rate move surfaces the affected cohort and the hedge action together.',
      assets: [
        { id: 'rre-t1', code: 'TRNCH-1', name: '30y fixed · prime', h2sPpm: 820 },
        { id: 'rre-t2', code: 'TRNCH-2', name: '15y fixed · prime', h2sPpm: 660 },
        { id: 'rre-t3', code: 'TRNCH-3', name: 'ARM 5/1', h2sPpm: 540 },
        { id: 'rre-t4', code: 'TRNCH-4', name: 'ARM 7/1', h2sPpm: 460 },
        { id: 'rre-t5', code: 'TRNCH-5', name: 'Jumbo fixed', h2sPpm: 380 },
        { id: 'rre-t6', code: 'TRNCH-6', name: 'Commercial mortgage', h2sPpm: 300 },
        { id: 'rre-t7', code: 'TRNCH-7', name: 'Multi-family', h2sPpm: 220 },
        { id: 'rre-t8', code: 'TRNCH-8', name: 'Reverse mortgage', h2sPpm: 160 },
        { id: 'rre-t9', code: 'TRNCH-9', name: 'Bridge loan', h2sPpm: 100 },
        { id: 'rre-t10', code: 'TRNCH-10', name: 'Construction loan', h2sPpm: 60 },
      ],
      currentThresholdPpm: 400,
      thresholdRange: { min: 50, max: 1000, step: 25 },
    },
    whatIf: {
      ...enLabDemos.whatIf,
      insightTitle: 'Rental yield, forward-projected',
      insightBody:
        'Property pro-formas used to live in static spreadsheets. The ontology projects rental yield under occupancy, rate, and capex scenarios — and flags any building whose forward yield would breach the investor mandate.',
      scenarios: [
        { id: 'normal', title: 'Baseline occupancy', description: 'Current rents · 95% occupancy assumption', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: 'Soft market', description: 'Concessions return · occupancy drift to 88%', h2sGrowthPctPerMonth: 3 },
        { id: 'extreme', title: 'Recession + tax change', description: 'Occupancy 80% · cap-rate widening · tax reform hits', h2sGrowthPctPerMonth: 6 },
      ],
      baseThresholdPpm: 85,
      assets: [
        { id: 'rre-b1', code: 'BLDG-1', name: 'Tower A · Class-A office', h2sPpmStart: 95 },
        { id: 'rre-b2', code: 'BLDG-2', name: 'Tower B · Class-A office', h2sPpmStart: 90 },
        { id: 'rre-b3', code: 'BLDG-3', name: 'Multi-family · 220 units', h2sPpmStart: 88 },
        { id: 'rre-b4', code: 'BLDG-4', name: 'Multi-family · 160 units', h2sPpmStart: 85 },
        { id: 'rre-b5', code: 'BLDG-5', name: 'Retail centre · A', h2sPpmStart: 82 },
        { id: 'rre-b6', code: 'BLDG-6', name: 'Retail centre · B', h2sPpmStart: 78 },
        { id: 'rre-b7', code: 'BLDG-7', name: 'Industrial · logistics', h2sPpmStart: 74 },
        { id: 'rre-b8', code: 'BLDG-8', name: 'Industrial · light mfg', h2sPpmStart: 70 },
      ],
    },
    riskScoring: {
      ...enLabDemos.riskScoring,
      controlLabel: 'Score property: 220 Riverwalk Tower',
      controlHint:
        'Adjust the three risk dimensions. The ontology aggregates weighted contributions into a single property risk score linking back to the rules and data sources behind every number.',
      dimensionsTitle: 'Property risk dimensions',
      dimensions: [
        { id: 'financial', label: 'Market & cap-rate', weightPct: 40, description: 'Cap-rate vs benchmark, market liquidity, rent growth', defaultValue: 5 },
        { id: 'operational', label: 'Building condition', weightPct: 35, description: 'Age, capex backlog, ESG retrofit need', defaultValue: 4 },
        { id: 'esg', label: 'Tenant mix & leases', weightPct: 25, description: 'Tenant credit, lease maturity wall, concentration', defaultValue: 3 },
      ],
      scoreTitle: 'Property risk score',
      insightTitle: 'Investment committee, not investment fiction',
      insightBody:
        'IC memos used to be 50-slide PDFs. With the ontology the committee gets the score, the reasoning chain, the comparable trades, and the sensitivity scenarios in one view — go / no-go in 30 minutes, not 3 weeks.',
      contextSupplier: {
        label: 'Property under review',
        name: '220 Riverwalk Tower',
        detail: 'Class-A office · 32 storeys · $480M target acquisition',
      },
    },
  },
}

// ───── Education ─────

const education: IndustryDemoPack = {
  demos: {
    goalDriven: {
      ...enLabDemos.goalDriven,
      wishesLabel: 'Student wishes',
      wishesHint: 'Pick a wish to watch the ontology compose the academic plan — courses, prerequisites, financial aid, advising — into one path.',
      insightTitle: 'Same wish, three plans, one ontology',
      insightBody:
        'Advising used to be three meetings and a spreadsheet. The ontology composes the path — major requirements, prerequisites, transfer credit, financial aid, advisor availability — from the student\'s wish, in seconds.',
      wishes: [
        { id: 'w1', goalId: 'compliance', quote: 'I want to graduate by Spring 2027 with a CS degree.', speaker: 'Aisha, junior' },
        { id: 'w2', goalId: 'uptime', quote: 'I want to switch from biology to data science without losing a semester.', speaker: 'Marco, sophomore' },
        { id: 'w3', goalId: 'cost', quote: 'I need a path that keeps my grant eligibility through summer.', speaker: 'Priya, freshman' },
      ],
    },
    documentDriven: {
      ...enLabDemos.documentDriven,
      documentsLabel: 'Inbound application documents',
      documentsHint: 'Click any doc to "ingest" it. Watch the LLM pull entities, the ontology bind them to the program, and the admission chain synthesise.',
      insightTitle: 'Three docs, one applicant chain',
      insightBody:
        'Transcript, recommendation letter, exam score all flow through the same ontology — different doc types fire different review chains. Programs see a unified applicant view, not a folder of PDFs.',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'Transcript-Patel-2024.pdf',
          meta: '4 pages · uploaded by applicant',
          summary: 'Undergraduate transcript · Lakeside University',
          entities: [
            { kind: 'Applicant', value: 'A. Patel · ID 2024-11824' },
            { kind: 'Major', value: 'Biology · minor Stats' },
            { kind: 'GPA', value: '3.78 / 4.0' },
            { kind: 'Notable', value: 'Honors thesis · 2 publications' },
            { kind: 'Transfer', value: '6 credits CS transferable' },
          ],
        },
        {
          id: 'rfq',
          icon: '✉',
          name: 'Recommendation-Prof-Lee.pdf',
          meta: '2 pages · uploaded by referee',
          summary: 'Strong recommendation · research advisor',
          entities: [
            { kind: 'Referee', value: 'Prof. Lee · Lakeside Bio' },
            { kind: 'Relationship', value: '2-year research advisor' },
            { kind: 'Rating', value: 'Top 5% of mentees' },
            { kind: 'Strengths', value: 'Research methods · independence' },
            { kind: 'Note', value: 'Pivot to CS supported' },
          ],
        },
        {
          id: 'incident',
          icon: '📊',
          name: 'GRE-Score-Report.pdf',
          meta: '1 page · uploaded by ETS',
          summary: 'Standardised test score · GRE',
          entities: [
            { kind: 'Test', value: 'GRE General · 2024-10' },
            { kind: 'Verbal', value: '165 / 170 · 95th pct' },
            { kind: 'Quant', value: '168 / 170 · 92nd pct' },
            { kind: 'Writing', value: '5.0 / 6.0' },
            { kind: 'Validity', value: 'Valid through 2029' },
          ],
        },
      ],
    },
    continuousAudit: {
      ...enLabDemos.continuousAudit,
      insightTitle: 'Accreditation as a heartbeat',
      insightBody:
        'Accreditation visits used to mean a six-month scramble. The ontology continuously evaluates programs against standards — course coverage, faculty credentials, assessment loops — and surfaces drift the day it happens, not the year of the visit.',
    },
  },
}

export const enIndustryDemos = {
  energy,
  finance,
  healthcare,
  legal,
  automotive,
  logistics,
  government,
  insurance,
  retail,
  realestate,
  education,
} as const
